import React, { useEffect, useState } from 'react'
import { omit, propEq } from 'ramda'

import i18n from '../../locales/i18n'
import { DocumentFile, FileTemplate, Nil } from '../../types'
import ButtonWithLoader, {
  ButtonWithLoaderProps,
} from '../buttons/ButtonWithLoader'
import SignatureDialog from './SignatureDialog'
import {
  addSignatureToDocumentBody,
  containsSignatureSlot,
} from './signatureUtils'

export interface SignButtonProps extends ButtonWithLoaderProps {
  readonly document: FileTemplate
  readonly iframeDocument?: Document | Nil
  readonly isLoading?: boolean
  readonly onCheckboxChange?: () => void
  readonly onSigned: (document: DocumentFile) => void
  readonly setSignatureSlotAvailable?: (signatureSlotAvailable: boolean) => void
  readonly signatureSlotAvailable?: boolean
}

const SignButton = ({
  document,
  iframeDocument,
  isLoading,
  onSigned,
  signatureSlotAvailable: signatureSlotAvailableProp = false,
  setSignatureSlotAvailable: setSignatureSlotAvailableProp,
  onCheckboxChange,
  ...rest
}: SignButtonProps) => {
  const [signatureSlotAvailable, setSignatureSlotAvailable] = useState(
    signatureSlotAvailableProp,
  )
  const [isSigned, setIsSigned] = useState(false)
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false)

  useEffect(() => {
    if (setSignatureSlotAvailableProp) {
      setSignatureSlotAvailableProp(signatureSlotAvailable)
    }
  }, [signatureSlotAvailable])

  useEffect(() => {
    if (!iframeDocument || !iframeDocument.body) {
      return
    }
    const inputs = Array.from(iframeDocument.getElementsByTagName('input'))
    const checkboxes = inputs.filter(propEq('type', 'checkbox'))
    const hasSignatureField = containsSignatureSlot(
      iframeDocument.body.innerHTML,
    )
    setSignatureSlotAvailable(hasSignatureField)
    checkboxes.forEach(node => {
      node.onchange = () => {
        if (node.checked) {
          node.setAttribute('checked', '')
        } else {
          node.removeAttribute('checked')
        }
        if (onCheckboxChange) {
          onCheckboxChange()
        }
      }
    })
  }, [iframeDocument])

  const handleDocumentSigned = (
    clientName: string,
    signatureString: string,
  ) => {
    if (iframeDocument?.body?.innerHTML) {
      iframeDocument.body.innerHTML = addSignatureToDocumentBody(
        iframeDocument.body.innerHTML,
        {
          clientName,
          signatureString,
        },
      )
      setSignatureDialogOpen(false)
      setSignatureSlotAvailable(false)
      setIsSigned(true)

      onSigned({
        ...omit(['fileUrl'], document),
        file: {
          extension: document.extension,
          body: iframeDocument.body.innerHTML,
        },
        signer: clientName,
        sign: true,
      })
    }
  }

  if (!signatureSlotAvailable && !isLoading) {
    return null
  }

  return (
    <>
      <ButtonWithLoader
        disabled={isLoading || isSigned}
        loading={isLoading}
        onClick={() => setSignatureDialogOpen(true)}
        {...rest}
      >
        {i18n.t<string>('Common:SIGNATURE.SIGN_ACTION')}
      </ButtonWithLoader>
      <SignatureDialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        onSign={handleDocumentSigned}
      />
    </>
  )
}

export default SignButton
